// User menu dropdown for authenticated users
import { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  SparklesIcon, 
  HeartIcon, 
  QuestionMarkCircleIcon, 
  DevicePhoneMobileIcon,
  ArrowRightOnRectangleIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';
import { apiFetch } from '../services/api';

export default function UserMenu() {
  const { user, subscription, hasActiveSubscription, signOut } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isDownloadingInvoice, setIsDownloadingInvoice] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!user) return null;

  const initials = user.name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const handleNavigation = (page: string) => {
    setIsOpen(false);
    window.location.href = `https://notescrafter.com/${page}`;
  };

  const handleDownloadInvoice = async () => {
    setIsDownloadingInvoice(true);
    try {
      const res = await apiFetch('/api/payment/invoice/latest?format=html');
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Invoice not available' }));
        throw new Error(err.error || 'Failed to open invoice');
      }
      const html = await res.text();
      const win = window.open('', '_blank');
      if (win) {
        win.document.open();
        win.document.write(html);
        win.document.close();
      }
    } catch (err: any) {
      alert(err.message || 'Invoice could not be loaded.');
    } finally {
      setIsDownloadingInvoice(false);
    }
  };

  const getPlanBadge = () => {
    if (!hasActiveSubscription || !subscription) return null;
    
    const plan = subscription.plan_type;
    switch (plan) {
      case 'monthly':
        return { 
          name: 'Silver', 
          class: 'from-slate-300 to-slate-500 shadow-slate-500/20', 
          icon: <span className="text-[7px]">S</span>
        };
      case 'half_yearly':
        return { 
          name: 'Gold', 
          class: 'from-amber-300 to-amber-500 shadow-amber-500/30', 
          icon: <span className="text-[7px]">G</span>
        };
      case 'annual':
        return { 
          name: 'Plat', 
          class: 'from-indigo-400 to-indigo-600 shadow-indigo-500/30', 
          icon: <span className="text-[7px]">P</span>
        };
      case 'lifetime':
        return { 
          name: 'Diamond Pass', 
          class: 'from-cyan-400/80 to-blue-600/80 backdrop-blur-md', 
          icon: (
            <div className="flex items-center gap-1 px-1.5 py-0.5">
              <div className="w-1 h-1 rounded-full bg-white animate-pulse" />
              <span className="text-[6px] font-black tracking-[0.1em] text-white uppercase">Diamond</span>
            </div>
          )
        };
      default:
        return null;
    }
  };

  const badge = getPlanBadge();

  return (
    <div ref={menuRef} className="relative">
      {/* Avatar Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 rounded-full transition-all duration-300 hover:scale-105 relative group"
      >
        <div className="relative flex items-center">
          <div className="relative p-0.5 rounded-full overflow-hidden">
            {/* Animated Ring for Premium Status */}
            {badge && (
              <div className={`absolute inset-0 bg-gradient-to-r ${badge.class} animate-spin-slow opacity-60`} />
            )}
            
            {user.profile_picture ? (
              <img
                src={user.profile_picture}
                alt={user.name}
                className={`w-9 h-9 rounded-full border-2 relative z-10 transition-all duration-300 ${badge ? 'border-slate-950/20' : 'border-primary/40'}`}
                referrerPolicy="no-referrer"
              />
            ) : (
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-white font-bold text-sm relative z-10">
                {initials}
              </div>
            )}
          </div>
        </div>
      </button>

      {/* Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: -5 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -5 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 mt-2 w-64 bg-white/95 dark:bg-[#0c0c14]/95 backdrop-blur-xl border border-black/10 dark:border-border-glow rounded-2xl shadow-2xl overflow-hidden z-50 transition-colors duration-200"
          >
            {/* User Info */}
            <div className="px-4 py-4 border-b border-black/[0.06] dark:border-border-glow">
              <p className="text-text-main font-semibold truncate">{user.name}</p>
              <p className="text-text-muted text-sm truncate">{user.email}</p>
              
              {/* Subscription badge */}
              {hasActiveSubscription && subscription ? (() => {
                const remainingDays = subscription.remaining_days ?? (
                  subscription.end_date 
                    ? Math.max(0, Math.floor((new Date(subscription.end_date).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
                    : 0
                );
                return (
                  <div className="mt-2 inline-flex items-center gap-1.5 px-3 py-1 bg-green-500/20 border border-green-500/40 rounded-full text-xs font-medium text-green-700 dark:text-green-300">
                    <span className="w-1.5 h-1.5 bg-green-500 dark:bg-green-400 rounded-full animate-pulse" />
                    {subscription.plan_type === 'lifetime' ? 'Lifetime Access' : `Community Pass · ${remainingDays} days left`}
                  </div>
                );
              })() : (
                <div className="mt-2 inline-flex items-center gap-1.5 px-3 py-1 bg-black/[0.04] dark:bg-white/[0.04] border border-black/10 dark:border-border-glow rounded-full text-xs font-medium text-text-muted">
                  No active pass
                </div>
              )}
            </div>

            {/* Menu Items */}
            <div className="py-2">
              {hasActiveSubscription && (
                <button
                  onClick={handleDownloadInvoice}
                  disabled={isDownloadingInvoice}
                  className="w-full px-4 py-2.5 text-left text-sm text-text-main hover:bg-black/[0.03] dark:hover:bg-glass/30 transition-colors flex items-center justify-between group cursor-pointer"
                >
                  <span className="flex items-center gap-2">
                    <DocumentTextIcon className="w-4 h-4 text-primary" />
                    <span>{isDownloadingInvoice ? 'Opening Invoice...' : 'Download Tax Invoice'}</span>
                  </span>
                  <span className="text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded bg-primary/10 text-primary border border-primary/20">
                    GST
                  </span>
                </button>
              )}

              {!hasActiveSubscription && (
                <button
                  onClick={() => handleNavigation('pricing')}
                  className="w-full px-4 py-2.5 text-left text-sm text-primary hover:bg-primary/10 transition-colors font-medium flex items-center gap-2 cursor-pointer"
                >
                  <SparklesIcon className="w-4 h-4" />
                  Get Community Pass
                </button>
              )}
              
              <button
                onClick={() => handleNavigation('support-project')}
                className="w-full px-4 py-2.5 text-left text-sm text-text-main hover:bg-black/[0.03] dark:hover:bg-glass/30 transition-colors flex items-center gap-2 cursor-pointer"
              >
                <HeartIcon className="w-4 h-4" />
                Support NotesCrafter
              </button>
              
              <button
                onClick={() => handleNavigation('help-center')}
                className="w-full px-4 py-2.5 text-left text-sm text-text-main hover:bg-black/[0.03] dark:hover:bg-glass/30 transition-colors flex items-center gap-2 cursor-pointer"
              >
                <QuestionMarkCircleIcon className="w-4 h-4" />
                Help Center
              </button>
              
              <a
                href="https://play.google.com/store/apps/details?id=com.notescrafter.app"
                className="w-full px-4 py-2.5 text-left text-sm text-text-main hover:bg-black/[0.03] dark:hover:bg-glass/30 transition-colors flex items-center gap-2"
                onClick={() => setIsOpen(false)}
              >
                <DevicePhoneMobileIcon className="w-4 h-4" />
                Download Android App
              </a>
            </div>

            {/* Sign Out */}
            <div className="border-t border-black/[0.06] dark:border-border-glow py-2">
              <button
                onClick={async () => {
                  setIsOpen(false);
                  await signOut();
                }}
                className="w-full px-4 py-2.5 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-500/10 transition-colors flex items-center gap-2 cursor-pointer"
              >
                <ArrowRightOnRectangleIcon className="w-4 h-4" />
                Sign Out
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
